import { request } from './client'

/* ============================================================================
   REPORTS — the framework half, on the client.

   ONE SCREEN, EIGHT REPORTS. W-110 is a picker, a period and a preview; the reports
   themselves are rows in a list the SERVER sends. Nothing here names a report, and that
   is the point: when the remaining seven definitions are settled with the client they
   appear on the screen without a line of client code, and a report this build has never
   heard of still renders, prints and downloads.

   WHICH REPORTS EXIST IS NOT THE SAME QUESTION AS WHICH REPORTS THIS PERSON MAY RUN, and
   the server answers the second one. A picker offering a store keeper eight reports of
   which he may open one is a picker that teaches him to distrust it — he clicks, is
   refused, and stops believing the other seven are real either.
   ========================================================================= */

export interface ReportDefinition {
  /** Language-neutral. What a URL and a permission are built from. */
  code: string
  name: string
  nameAr: string
  /** One line saying what question this answers. Drawn on the picker. */
  purpose: string
  purposeAr: string
  permission: string
  /**
   * A PERIOD or a MOMENT — and the screen must draw them differently.
   *
   * A period report is reproducible for ever; a moment report is a photograph of now
   * that can never be taken again. Offering date boxes for the second would promise
   * something the report cannot do.
   */
  isPeriod: boolean
}

/**
 * One rendered report — the DOCUMENT, not the rows.
 *
 * `html` is the report. The printed copy is this same markup through a browser on the
 * server, which is why there is no second shape here for "the data": a report with rows
 * on the client and markup on the server is two documents waiting to disagree.
 */
export interface ReportDocument {
  code: string
  title: string
  propertyName: string
  language: 'en' | 'ar'
  /** UNTRUSTED AS FAR AS THE DOM IS CONCERNED — see `Reports.tsx`. Shown in a sandboxed
   *  frame, never inserted into the application's own document. */
  html: string
  generatedAtUtc: string
  /** ASCII, dated, and the server's — so the file on disk and the stamp inside it agree. */
  fileName: string
  rowCount: number
  /**
   * THE PERIOD THE SERVER ACTUALLY USED, `YYYY-MM-DD`, inclusive at both ends. Null on
   * a moment report.
   *
   * The screen opens with the date boxes EMPTY and fills them from here, rather than
   * computing "the last seven days" itself: only the server knows what today is in
   * Najaf, and a browser still set to London would offer a supervisor the wrong week
   * for three hours every evening.
   */
  periodFrom: string | null
  periodTo: string | null
}

/** What a run is asked for. Every field optional — omitting the dates is how the caller
 *  says "whatever period you think", which is the honest thing to say on first opening. */
export interface ReportParameters {
  /** `YYYY-MM-DD`, the property's local date. */
  from?: string
  to?: string
  /** Omitted means the person's own preference, which the server holds. */
  language?: 'en' | 'ar'
}

export function fetchReports(
  propertyId: string,
  options: { signal?: AbortSignal } = {},
): Promise<ReportDefinition[]> {
  return request<ReportDefinition[]>(
    `/properties/${propertyId}/reports`,
    options.signal ? { signal: options.signal } : {})
}

export function renderReport(
  propertyId: string,
  code: string,
  parameters: ReportParameters = {},
  options: { signal?: AbortSignal } = {},
): Promise<ReportDocument> {
  return request<ReportDocument>(
    `/properties/${propertyId}/reports/${encodeURIComponent(code)}${query(parameters)}`,
    options.signal ? { signal: options.signal } : {})
}

/**
 * THE PRINTED COPY.
 *
 * Fetched rather than linked, for the same reason a photograph is: the download needs
 * the session header and a browser sends none on a plain navigation. Putting a token in
 * the query string to make a link work would write it into the access log and the
 * person's browser history.
 *
 * THE CALLER MUST REVOKE whatever object URL it makes of this. A report held open is a
 * multi-megabyte buffer that never leaves memory.
 */
export function fetchReportPdf(
  propertyId: string,
  code: string,
  parameters: ReportParameters = {},
  options: { signal?: AbortSignal } = {},
): Promise<Blob> {
  return request<Blob>(
    `/properties/${propertyId}/reports/${encodeURIComponent(code)}${query(parameters, 'pdf')}`,
    { expect: 'blob', ...(options.signal ? { signal: options.signal } : {}) })
}

/** The name a report draws under, in the language being read. */
export function reportName(report: ReportDefinition, locale: string): string {
  return locale === 'ar' ? report.nameAr : report.name
}

export function reportPurpose(report: ReportDefinition, locale: string): string {
  return locale === 'ar' ? report.purposeAr : report.purpose
}

/* BUILT WITH URLSearchParams, not by concatenation.

   Every value here is either a date the screen produced or a language code, so today
   nothing needs escaping — which is exactly the reasoning that puts an unescaped value
   in a URL the day a report gains a parameter somebody types. */
function query(parameters: ReportParameters, format?: 'pdf'): string {
  const search = new URLSearchParams()
  if (parameters.from) search.set('from', parameters.from)
  if (parameters.to) search.set('to', parameters.to)
  if (parameters.language) search.set('language', parameters.language)
  if (format) search.set('format', format)
  const text = search.toString()
  return text ? `?${text}` : ''
}
